import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomTooltip from '../../../../../components/tooltip/tooltip';
import { useAppSelector } from '../../../../../@types/redux';
import VarietyModelHelper from '../../../../../@types/model/masterData/variety/varietyModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { IVarietyFormValues } from '../../../../../@types/model/masterData/variety/varietyFormValues';
import OptionType from '../../../../../@types/model/optionType';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface IVarietyFormProps {
    isLoading : boolean;
    initialValues : IVarietyFormValues;
    onSubmit : (values : IVarietyFormValues) => void;
    onClose : () => void;
    isImported : boolean;
}

const VarietyForm = (props : IVarietyFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IVarietyFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(VarietyModelHelper.formSchema()),
        mode: 'onChange',
    });

    const isEditing = props.initialValues.id !== 0;

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const getTooltipTitle = (fieldName : string) => {
        if (props.isImported) {
            return 'Cannot change this field if variety is imported';
        }
        if (fieldName === 'commodity' && isEditing) {
            return 'Cannot change commodity for existing variety';
        }
        return '';
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <CustomTooltip className={'flx1'} title={getTooltipTitle('code')}>
                        <ControlledTextField
                            className={'mb10 flx1'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={props.initialValues.code}
                            disabled={props.isImported}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <CustomTooltip className={'flx1'} title={getTooltipTitle('name')}>
                        <ControlledTextField
                            className={'mb10 flx1'}
                            name={'name'}
                            label={'Name'}
                            control={control}
                            defaultValue={props.initialValues.name}
                            disabled={props.isImported}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <CustomTooltip title={getTooltipTitle('commodity')}>
                        <ControlledMuiAutoCompleteSelect
                            className={'mb10'}
                            name={'commodity'}
                            label={'Commodity'}
                            control={control}
                            options={commodityOptions}
                            defaultValues={props.initialValues.commodity ?? null}
                            disabled={props.isImported || isEditing}
                        />
                    </CustomTooltip>
                    <CustomTooltip title={getTooltipTitle('isActive')}>
                        <div className={'flx1 fdr jcfs'}>
                            <ControlledLabelledCheckBox
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={props.initialValues.isActive}
                                disabled={props.isImported || props.initialValues.isActive}
                            />
                        </div>
                    </CustomTooltip>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button
                            className={'cpd mr20'}
                            type={'reset'}
                            onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || props.isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'}
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control}/>
        </div>
    );
};

export default VarietyForm;
